import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ecrireHashPanneau, estOnglet, leadVoisin, lirePanneauDepuisHash, ongletParDefaut } from "./etatPanneau";

export const CLASSE_PANNEAU_OUVERT = "la-panneau-ouvert";

// État du panneau de détail au niveau d'une liste de leads : un seul lead
// ouvert, sur un onglet parmi ceux de la page (`onglets`). Posé dans le
// contexte des cartes ; c'est la carte du lead ouvert qui rend le panneau,
// pour garder ses données et ses actions. L'adresse reflète l'état
// (#panneau=<lead>:<onglet>) : un rechargement rouvre le même lead, et la
// classe sur <body> fait de la place à droite sur grand écran (carteLead.scss).
export default function usePanneauLeads(leads, onglets) {
  const ids = useMemo(() => (leads || []).map((l) => l.id), [leads]);
  const [etat, setEtat] = useState({ leadId: null, onglet: ongletParDefaut(onglets) });
  const restaure = useRef(false);
  const carteSuivie = useRef(null);

  // Restauration depuis l'adresse, une fois la liste chargée.
  useEffect(() => {
    if (restaure.current || !leads) return;
    restaure.current = true;
    const lu = lirePanneauDepuisHash(window.location.hash, onglets);
    if (lu && ids.includes(lu.leadId)) setEtat(lu);
  }, [leads, ids, onglets]);

  // Le lead ouvert n'est plus dans la liste (autre page, autre filtre) : fermé.
  useEffect(() => {
    if (leads && etat.leadId && !ids.includes(etat.leadId)) {
      setEtat((e) => ({ ...e, leadId: null }));
    }
  }, [leads, ids, etat.leadId]);

  // Sur grand écran, la classe resserre la liste : toutes les cartes changent de
  // hauteur et, à défilement égal, la carte du lead part loin de l'écran
  // (ST1-2220). La classe est posée avant l'affichage et le défilement compensé
  // pour que la carte qu'on ouvre, ou celle qu'on referme, reste à sa place.
  useLayoutEffect(() => {
    const ouvert = Boolean(etat.leadId);
    const carte = ouvert
      ? document.querySelector(".la-carte--ouverte")
      : carteSuivie.current && document.getElementById(carteSuivie.current);
    carteSuivie.current = ouvert && carte ? carte.id : null;
    const avant = carte ? carte.getBoundingClientRect().top : null;
    document.body.classList.toggle(CLASSE_PANNEAU_OUVERT, ouvert);
    if (avant === null) return;
    const decalage = carte.getBoundingClientRect().top - avant;
    if (!decalage) return;
    // Saut instantané : le défilement doux de la page (buttons.scss) rendrait la
    // compensation visible. Pas de `behavior: "instant"`, refusé par d'anciens Safari.
    const racine = document.documentElement;
    racine.style.scrollBehavior = "auto";
    window.scrollBy(0, decalage);
    racine.style.scrollBehavior = "";
  }, [etat.leadId]);

  useEffect(() => {
    const ouvert = Boolean(etat.leadId);
    const base = window.location.pathname + window.location.search;
    if (ouvert) {
      const cible = base + ecrireHashPanneau(etat.leadId, etat.onglet, onglets);
      if (cible !== base + window.location.hash) window.history.replaceState(window.history.state, "", cible);
    } else if (restaure.current && lirePanneauDepuisHash(window.location.hash, onglets)) {
      // Panneau refermé : on retire notre hash, et seulement le nôtre. Avant la
      // restauration, le hash d'arrivée est laissé en place pour être lu.
      window.history.replaceState(window.history.state, "", base);
    }
  }, [etat.leadId, etat.onglet, onglets]);

  useEffect(() => () => document.body.classList.remove(CLASSE_PANNEAU_OUVERT), []);

  const ouvrir = useCallback(
    (leadId, onglet) => {
      setEtat({ leadId, onglet: estOnglet(onglet, onglets) ? onglet : ongletParDefaut(onglets) });
    },
    [onglets]
  );
  const fermer = useCallback(() => setEtat((e) => ({ ...e, leadId: null })), []);
  const changerOnglet = useCallback(
    (onglet) => {
      if (estOnglet(onglet, onglets)) setEtat((e) => ({ ...e, onglet }));
    },
    [onglets]
  );
  const naviguer = useCallback(
    (delta) =>
      setEtat((e) => {
        const voisin = leadVoisin(ids, e.leadId, delta);
        return voisin ? { ...e, leadId: voisin } : e;
      }),
    [ids]
  );

  return useMemo(
    () => ({ leadId: etat.leadId, onglet: etat.onglet, ids, onglets, ouvrir, fermer, changerOnglet, naviguer }),
    [etat.leadId, etat.onglet, ids, onglets, ouvrir, fermer, changerOnglet, naviguer]
  );
}
